import { AddRounded, RemoveRounded } from '@mui/icons-material'
import { Button, Grid, InputBase, MenuItem, Popover, Typography } from '@mui/material'
import React, { Dispatch, memo, SetStateAction, useCallback, useMemo, useState } from 'react'
import { addDaysToDate, findNeighbor } from '../../util/tradepanel'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { useSelector } from 'react-redux'
import { selectSimulatorDate } from '../../selectors/simulator'

export type Props = {
    numberOfDaysToExpire: number
    targetDaysFromToday: number
    numberOfDaysToExpireArray: number[]
    setTargetDaysFromToday: Dispatch<SetStateAction<number>>
    simulator: boolean
}

const TargetDayView = ({
    numberOfDaysToExpire,
    numberOfDaysToExpireArray,
    targetDaysFromToday,
    setTargetDaysFromToday,
    simulator
}: Props) => {
    const [popupAnchorEl, setPopupAnchorEl] = useState<HTMLElement | null>(null)
    const simulatorTimestamp = useSelector(selectSimulatorDate)

    const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setPopupAnchorEl(event.currentTarget)
    }, [])

    const handleMinusTargetDays = useCallback(() => {
        AnalyticsManager.logEvent("Target Days Changed", { source: 'strategy-builder-submenu', type: 'decrement' })
        // if (targetDaysFromToday > 0) setTargetDaysFromToday(targetDaysFromToday - 1)
        const newDays = findNeighbor(numberOfDaysToExpireArray, targetDaysFromToday, 'left')
        setTargetDaysFromToday(newDays)
    }, [setTargetDaysFromToday, targetDaysFromToday])

    const handlePlusTargetDays = useCallback(() => {
        AnalyticsManager.logEvent("Target Days Changed", { source: 'strategy-builder-submenu', type: 'increment' })
        // if (targetDaysFromToday < numberOfDaysToExpire) setTargetDaysFromToday(targetDaysFromToday + 1)
        const newDays = findNeighbor(numberOfDaysToExpireArray, targetDaysFromToday, 'right')
        setTargetDaysFromToday(newDays)
    }, [targetDaysFromToday, numberOfDaysToExpire, setTargetDaysFromToday])

    const handleTargetDayClick = useCallback((i: number) => {
        setTargetDaysFromToday(i)
        setPopupAnchorEl(null)
    }, [setTargetDaysFromToday])

    const handlePopoverClose = useCallback(() => {
        setPopupAnchorEl(null)
    }, [])

    const popover = useMemo(() => {
        return (
            <Popover
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                anchorEl={popupAnchorEl}
                open={Boolean(popupAnchorEl)}
                onClose={handlePopoverClose}
            >
                {numberOfDaysToExpireArray.map((i) => (
                    <MenuItem data-event={'Target Days Selected'} data-event-source={'strategy-builder-submenu'} key={i.toString()} onClick={handleTargetDayClick.bind(null, i)}>
                        {addDaysToDate(i, simulator ? simulatorTimestamp : undefined)}
                    </MenuItem>
                ))}
            </Popover>
        )
    }, [popupAnchorEl, handlePopoverClose, numberOfDaysToExpireArray, handleTargetDayClick, simulator, simulatorTimestamp])

    const targetDateText = useMemo(() => {
        return (
            <Typography sx={{ fontSize: '12px', paddingX: 1 }}>
                DATE
            </Typography>
        )
    }, [])

    const minusButton = useMemo(() => {
        return (
            <div className="order-buttons" style={{ width: '21px', cursor: 'pointer' }} onClick={handleMinusTargetDays}>
                <RemoveRounded className="trade-panel-edit-icons" />
            </div>)
    }, [handleMinusTargetDays])

    const targetDaysInputBase = useMemo(() => {
        return (<InputBase
            value={addDaysToDate(targetDaysFromToday, simulator ? simulatorTimestamp : undefined) + `(${numberOfDaysToExpire - targetDaysFromToday}D)`}
            className="order-custom-input"
            onClick={handleOpenPopover}
            sx={{
                height: '100%',
                textAlign: 'center',
                backgroundColor: 'none',
                cursor: 'pointer',
                width: '110px',
                fontSize: '14px',
                '& .MuiInputBase-input': { padding: '4px 0 5px' }
            }}
        />)
    }, [handleOpenPopover, numberOfDaysToExpire, simulator, simulatorTimestamp, targetDaysFromToday])

    const plusButton = useMemo(() => {
        return (
            <div className="order-buttons" style={{ width: '21px', cursor: 'pointer' }} onClick={handlePlusTargetDays}>
                <AddRounded className="trade-panel-edit-icons" />
            </div>)
    }, [handlePlusTargetDays])


    return (
        <Grid item id="target-day-container" sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <Grid item xs={12} textAlign={'left'}>
                <Typography variant="caption" component="span" color={'text.secondary'} className="trade-panel-text">
                    {targetDateText}
                </Typography>
            </Grid>
            <Grid item xs={12} textAlign={'center'}>
                <Typography className="trade-panel-boldest-text" variant="body2" component="span" color="primaryText" fontSize={'small'} sx={{ fontSize: '10px' }}>
                    <Button variant="outlined" color={'inherit'} sx={{
                        height: '24px',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'row !important',
                        alignItems: 'start !important'
                    }}>
                        {minusButton}
                        {targetDaysInputBase}
                        {plusButton}
                    </Button>
                    {popover}
                </Typography>
            </Grid>
        </Grid>
    )
}

export default memo(TargetDayView)
